import "./pricing-plan-selection.css";
import useAppContext from "../../hooks/useAppContext";
import React, {useEffect, useState} from "react";
import {getPricingPlans} from "../../api/getPricingPlans";
import useUserContext from "../../hooks/useUserContext";
import {PricingPlanProps} from "../../types/PricingPlanProps";
import Loader from "../loader/Loader";
import Spaceship1 from "../../assets/images/spaceship-1.png";
import Spaceship2 from "../../assets/images/spaceship-2.png";
import Spaceship3 from "../../assets/images/spaceship-3.png";
import Spaceship4 from "../../assets/images/spaceship-4.png";
import SpaceshipStars from "../../assets/images/spaceship-stars.png";
import {CheckCircleOutlined, CloseCircleOutlined, MobileOutlined} from "@ant-design/icons";
import {Button, Modal} from "antd";
import Crown from "../../assets/icons/PlanCrown.png";
import CountriesSelector from "../contriesSelector/CountriesSelector";
import useToast from "../../hooks/useToast";
import {ToastSkin} from "../../context/ToastContext";
import {getNextCyclePricingPlan} from "../../api/getNextCyclePricingPlan";
import usePaymentFlow from "../../hooks/usePaymentFlow";
import BurgerMenu from "../burgerMenu/BurgerMenu";
import {cancelPricingPlan} from "../../api/cancelPricingPlan";
import {getAvailablePhoneCountries} from "../../api/getAvailablePhoneCountries";
import {countryCodesList} from "../../utils/countryCodesList";
import {isFreePricingPlanId} from "../../consts/freePricingPlans";

interface PricingPlanSelectionProps {
    isFullDisplay?: boolean;
    iconSize?: number;
    onSelect?: () => void;
    isInClosedModal?: boolean;
}

const availablePhoneCountriesCache = new Map<string, string[]>();
const availablePhoneCountriesInflight = new Map<string, Promise<string[]>>();

const PricingPlanSelection = ({ isFullDisplay = true, iconSize = 100, onSelect, isInClosedModal = false }: PricingPlanSelectionProps) => {
    const [pricingPlans, setPricingPlans] = useState<PricingPlanProps[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { selectedDomain } = useUserContext();
    const spaceships: Record<number, string> = {
        2: Spaceship1,
        3: Spaceship2,
        4: Spaceship3,
        5: Spaceship4,
        6: Spaceship1,
    };
    const { data: { shop, waba } } = useAppContext();
    const selectedPricingPlanId = shop.data?.pricingPlan?.id;
    const [isApprovePricingPlanChangeOn, setIsApprovePricingPlanChangeOn] = useState(false);
    const [isApproveFreePricingPlanChangeOn, setIsApproveFreePricingPlanChangeOn] = useState(false);
    const [pendingPricingPlanId, setPendingPricingPlanId] = useState<number | null>(null);
    const [iso, setIso] = useState('US');
    const hasDedicatedPlan = !!pricingPlans?.find(pp => pp.isDedicatedToShopId);
    const { showToast, closeToast } = useToast();
    const { startPricingPlanFlow } = usePaymentFlow();
    const currentPricingPlan = shop?.data?.pricingPlan;
    const hasCurrentPaidPlan = !!currentPricingPlan?.id && currentPricingPlan.id !== 1;
    const [nextCyclePricingPlanId, setNextCyclePricingPlanId] = useState(null);
    const [isCancellingPlan, setIsCancellingPlan] = useState(false);
    const [availablePhoneCountries, setAvailablePhoneCountries] = useState<string[]>([]);
    const [isLoadingAvailablePhoneCountries, setIsLoadingAvailablePhoneCountries] = useState(false);

    const loadCycleChanges = async () => {
        if(selectedDomain) {
            const nextCycleResponse = await getNextCyclePricingPlan(selectedDomain);
            if (nextCycleResponse?.data?.nextPricingPlanId) {
                setNextCyclePricingPlanId(nextCycleResponse.data.nextPricingPlanId);
            } else {
                setNextCyclePricingPlanId(null);
            }
        }
    }

    const loadPricingPlans = async () => {
        if(!selectedDomain) return;
        setIsLoading(true);
        const response = await getPricingPlans(selectedDomain, iso);
        loadCycleChanges();
        setPricingPlans(response.data);
        setIsLoading(false);
    }

    const loadAvailablePhoneCountries = async () => {
        if (!selectedDomain) return;
        const cachedLocales = availablePhoneCountriesCache.get(selectedDomain);
        if (cachedLocales) {
            setAvailablePhoneCountries(cachedLocales);
            return;
        }

        let inflight = availablePhoneCountriesInflight.get(selectedDomain);
        if (!inflight) {
            inflight = (async () => {
                const response = await getAvailablePhoneCountries(
                    selectedDomain,
                    countryCodesList.map(item => item.country).filter(country => country !== 'US')
                );
                const locales = Array.isArray(response?.data) ? response.data : [];
                availablePhoneCountriesCache.set(selectedDomain, locales);
                return locales;
            })();
            availablePhoneCountriesInflight.set(selectedDomain, inflight);
        }

        setIsLoadingAvailablePhoneCountries(true);
        try {
            const locales = await inflight;
            setAvailablePhoneCountries(locales);
        } finally {
            availablePhoneCountriesInflight.delete(selectedDomain);
            setIsLoadingAvailablePhoneCountries(false);
        }
    }

    const onConfirm = async (event: any) => {
        if (event?.data?.billingConfirmed) {
            console.log("Billing confirmed!");
            if(onSelect) {
                beforeOnSelect();
                onSelect();
            } else {
                window.location.reload();
            }
        }
    }

    useEffect(() => {
        loadPricingPlans();
    }, [selectedDomain, iso]);

    useEffect(() => {
        loadAvailablePhoneCountries();
    }, [selectedDomain]);

    useEffect(() => {
        if (iso !== 'US' && availablePhoneCountries.length && !availablePhoneCountries.includes(iso)) {
            setIso('US');
        }
    }, [availablePhoneCountries, iso]);

    useEffect(() => {
        const ch = new BroadcastChannel('vuelve-billing');
        ch.addEventListener("message", onConfirm);
        return () => {
            ch.removeEventListener("message", onConfirm);
        }
    }, [selectedDomain]);

    const getSupportLevelStr = (supportLevel: number) => {
        if(supportLevel === 1) return 'Email support only';
        if(supportLevel === 2) return 'Chat Support';
        if(supportLevel === 3) return 'Priority Chat Support';
        if(supportLevel === 4) return 'Priority Chat Support + Optional dedicated account manager';
    }

    const getCTA = (id: number) => {
        if(id === 3) return 'Start Growing';
        if(id === 4) return 'Scale My Store';
        if(id === 5) return 'Talk to Sales';
        return 'Get Started';
    }

    const getV2ExtraUsageLabel = (allowExtraCharge: boolean) => {
        return allowExtraCharge ? 'Low-cost extra usage' : 'No extra usage charges';
    }

    const beforeOnSelect = async () => {
        if(selectedDomain) {
            console.log("load nextCycle again...");
            loadCycleChanges();
        }
    }

    const onChangingPricingPlan = async (pricingPlanId: number) => {
        const newPricingPlan = pricingPlans?.find(pp => pp.id === pricingPlanId);
        await startPricingPlanFlow({
            selectedDomain,
            platformId: shop.data?.platformId,
            pricingPlanId,
            title: `${newPricingPlan?.title || ''} Pricing Plan`,
            amountUsd: (newPricingPlan?.fee || 0) - (currentPricingPlan?.fee || 0),
            description: (newPricingPlan?.fee || 0) > (currentPricingPlan?.fee || 0) ? <>
                <div className="reduct-price">This cycle: Only the difference - ${Math.round(((newPricingPlan?.fee || 0) - (currentPricingPlan?.fee || 0)) * 1000) / 1000}</div>
                <div className="monthly-price">Next cycle: Monthly price - ${Math.round((newPricingPlan?.fee || 0) * 1000) / 1000}</div>
            </> : (newPricingPlan?.fee || 0) < (currentPricingPlan?.fee || 0) ? <>
                <div className="note">Your previous plan stays active until the next cycle.</div>
                <div className="price">${(newPricingPlan?.fee || 0)} <span>Starting next billing cycle</span></div>
            </> : null,
            onSuccess: afterSuccessPlanSwitch,
            onDirectSuccess: () => {
                if (onSelect) {
                    beforeOnSelect();
                    onSelect();
                } else {
                    window.location.reload();
                }
            },
        });
    }

    const onApprovingPricingPlanChange = async () => {
        if(pendingPricingPlanId) {
            await onChangingPricingPlan(pendingPricingPlanId);
            setPendingPricingPlanId(null);
            setIsApprovePricingPlanChangeOn(false);
            setIsApproveFreePricingPlanChangeOn(false);
        }
    }

    const onPricingPlanClick = async (pricingPlanId: number) => {
        const currentPricingPlan: PricingPlanProps = shop?.data?.pricingPlan;
        const newPricingPlan = pricingPlans?.find(pricingPlan => pricingPlan.id === pricingPlanId);
        const phoneNumber = waba?.data?.waba?.phones?.[0];
        if(phoneNumber && !phoneNumber.isByon && currentPricingPlan.isPhoneNumberIncluded && !newPricingPlan?.isPhoneNumberIncluded) {
            setPendingPricingPlanId(pricingPlanId);
            setIsApprovePricingPlanChangeOn(true);
        } else if(phoneNumber && !phoneNumber.isByon && !isFreePricingPlanId(currentPricingPlan.id) && isFreePricingPlanId(newPricingPlan?.id) && phoneNumber.iso !== 'US') {
            setPendingPricingPlanId(pricingPlanId);
            setIsApproveFreePricingPlanChangeOn(true);
        } else {
            await onChangingPricingPlan(pricingPlanId);
        }
    }

    const afterSuccessPlanSwitch = (successDetails: any) => {
        console.log("successDetails: ", successDetails);
        const same = successDetails?.same;
        const downgrade = successDetails?.downgrade;
        const invoiceNumber = successDetails?.invoiceNumber;
        console.log("same: ", same);
        if (onSelect) {
            const text = downgrade ?
                'Your pricing plan was updated. You’ll continue enjoying your previous plan’s benefits until the end of your billing cycle.' :
                same ? 'You’re all set! Your current plan will stay active with no changes at the end of this cycle.' :
                `Payment successful! Your pricing plan is now active.${invoiceNumber ? ` Invoice No. ${invoiceNumber} has been issued.` : ''}`;
            showToast({ skin: ToastSkin.approval, displayTime: 15000, text, buttons: [
                { text: 'Ok', onClick: closeToast },
            ]});
            beforeOnSelect();
            onSelect();
        } else {
            window.location.reload();
        }
    }

    const afterCancelSuccess = async () => {
        await loadCycleChanges();
        if (shop?.data?.platformId === 1) {
            showToast({
                skin: ToastSkin.approval,
                displayTime: 12000,
                text: "Your subscription was cancelled and your store access is being disconnected now.",
                buttons: [{ text: "Ok", onClick: closeToast }],
            });
            if (onSelect) {
                onSelect();
            } else {
                window.location.reload();
            }
            return;
        }

        showToast({
            skin: ToastSkin.approval,
            displayTime: 15000,
            text: `Your ${currentPricingPlan?.title || "current"} subscription was cancelled. Your current plan benefits remain active until the end of this billing cycle.`,
            buttons: [{ text: "Ok", onClick: closeToast }],
        });
        if (onSelect) {
            onSelect();
        }
    };

    const onCancelPlan = async () => {
        if (!selectedDomain || !currentPricingPlan?.id || currentPricingPlan.id === 1 || isCancellingPlan) return;
        setIsCancellingPlan(true);
        const response = await cancelPricingPlan(selectedDomain);
        setIsCancellingPlan(false);
        if (response) {
            await afterCancelSuccess();
        } else {
            showToast({
                skin: ToastSkin.error,
                text: "Failed to cancel subscription. Please try again.",
                buttons: [{ text: "Ok", onClick: closeToast }],
            });
        }
    };

    const onCancelPlanClick = () => {
        if (!currentPricingPlan?.id || currentPricingPlan.id === 1) return;
        showToast({
            skin: ToastSkin.danger,
            title: `Cancel ${currentPricingPlan.title} subscription?`,
            text: shop?.data?.platformId === 1
                ? "This will cancel the Shopify subscription immediately and disconnect your current phone number and WhatsApp account."
                : "This will cancel your subscription at the end of the current billing cycle. Your phone number and WhatsApp account will be disconnected at that time.",
            buttons: [
                { text: "Keep Plan", onClick: closeToast },
                { text: isCancellingPlan ? "Cancelling..." : "Cancel Subscription", onClick: onCancelPlan },
            ],
        });
    };

    const menuItems = hasCurrentPaidPlan && nextCyclePricingPlanId !== 1 ? [{
        id: "cancel-subscription",
        label: `Cancel ${currentPricingPlan.title} subscription`,
        isDestructive: true,
        onClick: onCancelPlanClick,
    }] : [];

    const isUsPhoneSelection = iso === 'US';
    const baseVisiblePlans = pricingPlans?.filter(pricingPlan => pricingPlan.isDedicatedToShopId || !isFreePricingPlanId(pricingPlan.id) || !hasDedicatedPlan) || [];
    const bucketVisiblePlans = baseVisiblePlans.filter(pricingPlan => {
        if (pricingPlan.isDedicatedToShopId) return true;
        if (isUsPhoneSelection) {
            return pricingPlan.includedPhoneNumberIso === 'US';
        }
        return !pricingPlan.includedPhoneNumberIso;
    });
    const visiblePricingPlans = bucketVisiblePlans.length ? bucketVisiblePlans : baseVisiblePlans;
    const availableCountryOptions = countryCodesList
        .filter(country => country.country === 'US' || availablePhoneCountries.includes(country.country))
        .map(country => ({
            ...country,
            customLabel: country.country === 'US' ? 'US number or your own number' : country.countryName,
        }));

    return isLoading ? <Loader size={150} /> :
        <div className="pricing-plan-selection-container">
            {isInClosedModal && hasCurrentPaidPlan && menuItems.length ? <div className="pricing-plan-modal-actions">
                <BurgerMenu
                    menuId={`pricing-plan-selection-${selectedDomain || "shop"}`}
                    items={menuItems}
                />
            </div> : null}
            {isFullDisplay ?
                <div className="headline">
                    <div className="headline-row">
                        <div className="title">Choose the Right Plan for Your Store</div>
                        {!isInClosedModal && hasCurrentPaidPlan && menuItems.length ? <BurgerMenu
                            menuId={`pricing-plan-selection-${selectedDomain || "shop"}`}
                            items={menuItems}
                        /> : null}
                    </div>
                </div>
            : <div className={`headline ${!isInClosedModal && hasCurrentPaidPlan && menuItems.length ? 'headline-inline-actions' : ''}`}>
                {!isInClosedModal && hasCurrentPaidPlan && menuItems.length ? <div className="headline-row">
                    <div />
                    <BurgerMenu
                        menuId={`pricing-plan-selection-${selectedDomain || "shop"}`}
                        items={menuItems}
                    />
                </div> : null}
                <div className="sub-title pricing-plan-country-sub-title">
                    <div>Pricing plans vary by your phone number selection:</div>
                    <CountriesSelector
                        selectedCountry={iso || 'US'}
                        setSelectedCountry={(newCountry) => setIso(newCountry)}
                        valueProp="country"
                        isDisabled={isLoadingAvailablePhoneCountries}
                        options={availableCountryOptions}
                        width={380}
                    />
                </div>
            </div>}
            <div className="list">
                {visiblePricingPlans.map((pricingPlan: PricingPlanProps, index: number) => <div className={`pricing-plan-container relative ${pricingPlan.id === selectedPricingPlanId ? 'selected' : ''} ${pricingPlan.id === nextCyclePricingPlanId ? 'selected' : ''}`} key={`pricing-plan-${pricingPlan.id}`}>
                    {pricingPlan.id === 4 ? <div className="badge-icon"><img src={Crown} width={50} height={50} /></div> : null}
                    {pricingPlan.id === selectedPricingPlanId ? <div className="selected-plan">{nextCyclePricingPlanId === 1 ? "Your current plan - cancellation scheduled" : "Your current plan"}</div> : null}
                    {pricingPlan.id === nextCyclePricingPlanId ? <div className="selected-plan next-plan">Your next plan</div> : null}
                    <div className={`pricing-plan ${selectedPricingPlanId && pricingPlan.id === selectedPricingPlanId ? 'selected' : ''} ${nextCyclePricingPlanId && pricingPlan.id === nextCyclePricingPlanId ? 'selected next-plan' : ''}`} onClick={() => (pricingPlan.id !== selectedPricingPlanId || (pricingPlan.id === selectedPricingPlanId && nextCyclePricingPlanId && pricingPlan.id !== nextCyclePricingPlanId)) && onPricingPlanClick(pricingPlan.id)}>
                        <div className="relative" style={{ width: '100%' }}>
                            <div className="pricing-plan-headline">
                                <div className="title">{pricingPlan.title}</div>
                                <div className="price-title">{pricingPlan.id === 5 ? 'Starting from ' : ''}${pricingPlan.fee}/Month</div>
                                <img src={SpaceshipStars} width={170} height={60} className="spaceship-stars" />
                                {pricingPlan.id === 4 ? <div className="badge"><div>Most</div><div>Popular!</div></div> : null}
                            </div>
                            <img src={spaceships[pricingPlan.id > 6 ? 5 : pricingPlan.id]} width={iconSize} height={iconSize} />
                            <div className="description">{pricingPlan.description}</div>

                            <div className="features-list">
                                {
                                    isFreePricingPlanId(pricingPlan.id) ? null : pricingPlan.isV2 ? <div className="included">
                                        <MobileOutlined width={24} height={24} />
                                        <div>Marketing Msgs: <b>{pricingPlan.freeCreditPerCycle || 0}</b></div>
                                    </div> : null
                                }
                                {
                                    isFreePricingPlanId(pricingPlan.id) ? null : pricingPlan.isV2 ? <div className="included">
                                        <MobileOutlined width={24} height={24}/>
                                        <div>Chat Msgs: <b>{pricingPlan.freeConversationMessagesPerCycle || 0}</b></div>
                                    </div> : null
                                }
                                {
                                    isFreePricingPlanId(pricingPlan.id) ? null : pricingPlan.isV2 ? <div className="included">
                                        <MobileOutlined width={24} height={24}/>
                                        <div>{getV2ExtraUsageLabel(!!pricingPlan.allowExtraCharge)}</div>
                                    </div> : null
                                }
                                <div className="included">
                                    <CheckCircleOutlined width={24} height={24} />
                                    <div>{pricingPlan.isPhoneNumberIncluded ? <><b>Dedicated</b> phone number</> : <><b>Bring your own number</b>, or add one for an <b>extra $19/month</b></>}</div>
                                </div>
                                {
                                    isFreePricingPlanId(pricingPlan.id) ? <div className="included">
                                        <MobileOutlined width={24} height={24} />
                                        <div><b>Free</b> 20 monthly messages</div>
                                    </div> : null
                                }
                                <div className="included">
                                    <CheckCircleOutlined width={24} height={24} />
                                    <div>{pricingPlan.campaignLimit === -1 ? <><b>Unlimited</b> campaigns</> : `${pricingPlan.campaignLimit} monthly campaign included`}</div>
                                </div>
                                <div className="included">
                                    <CheckCircleOutlined width={24} height={24} />
                                    <div><b>Mobile App</b> Access</div>
                                </div>
                                <div className="included">
                                    <CheckCircleOutlined width={24} height={24} />
                                    <div>Analytics, Reports & Logs</div>
                                </div>
                                {
                                    isFreePricingPlanId(pricingPlan.id) || pricingPlan.isV2 ? null : <div className="included">
                                        <MobileOutlined width={24} height={24} />
                                        <div>Marketing Msg: <b>${pricingPlan.messageCost}</b></div>
                                    </div>
                                }
                                {
                                    isFreePricingPlanId(pricingPlan.id) || pricingPlan.isV2 ? null : <div className="included">
                                        <MobileOutlined width={24} height={24}/>
                                        <div>Chat Msg: <b>${pricingPlan.conversationCost}</b></div>
                                    </div>
                                }
                                {
                                    isFreePricingPlanId(pricingPlan.id) || pricingPlan.isV2 ? null : <div className="included">
                                        <MobileOutlined width={24} height={24}/>
                                        <div>Recovery commission: <b>{pricingPlan.recoveryCommission * 100}%</b></div>
                                    </div>
                                }
                                <div className={pricingPlan.customIntegration ? 'included' : ''}>
                                    {pricingPlan.customIntegration ? <CheckCircleOutlined width={24} height={24} /> : <CloseCircleOutlined width={24} height={24} />}
                                    <div>Custom Integrations</div>
                                </div>
                                <div className={pricingPlan.customFeature ? 'included' : ''}>
                                    {pricingPlan.customFeature ? <CheckCircleOutlined width={24} height={24} /> : <CloseCircleOutlined width={24} height={24} />}
                                    <div>Custom Feature</div>
                                </div>
                                {pricingPlan.trial ? <div className='included'>
                                    <CheckCircleOutlined width={24} height={24} />
                                    <div><b>{pricingPlan.trial} days trial</b> without monthly fee</div>
                                </div> : null}
                                <div className='included'>
                                    <CheckCircleOutlined width={24} height={24} />
                                    <div>{getSupportLevelStr(pricingPlan.supportLevel)}</div>
                                </div>
                            </div>
                        </div>
                        <Button size="large" type="default" disabled={selectedPricingPlanId === pricingPlan.id}>{getCTA(pricingPlan.id)}</Button>
                    </div>
                </div>)}
            </div>

            <Modal open={!!pendingPricingPlanId && isApprovePricingPlanChangeOn} title="Important Plan Change Notice" onCancel={() => setIsApprovePricingPlanChangeOn(false)} onOk={onApprovingPricingPlanChange} confirmLoading={isLoading}>
                You are switching to a plan that does not include a phone number. Since your current phone number was provided by us, it will now be billed separately at $19 per month. The first charge will be applied immediately and is non-refundable.
            </Modal>

            <Modal open={!!pendingPricingPlanId && isApproveFreePricingPlanChangeOn} title="Important change to your phone number" onCancel={() => setIsApproveFreePricingPlanChangeOn(false)} onOk={onApprovingPricingPlanChange} confirmLoading={isLoading}>
                <div>Switching to the Free plan will replace your current WhatsApp number with a free US number provided by us.</div>
                <div>This means you’ll need to:</div>
                <ul>
                    <li>Re-complete Meta onboarding</li>
                    <li>Re-create your message templates</li>
                </ul>
                <div>All existing templates and onboarding tied to your current number will be lost.</div>
                <div>Do you want to continue?</div>
            </Modal>
        </div>
}

export default PricingPlanSelection;
